import { useState, useEffect, useCallback } from 'react';
import { Loader2, Inbox, MailCheck, MailX, Send, Copy, RefreshCw } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import OrderStatusBadge from '@/components/OrderStatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

const STATUSES = ['received', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const fmtDate = (iso) => new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });

const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get('/api/admin/orders');
    setOrders(data.orders);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/api/admin/orders/${order.id}/status`, { status });
      toast.success(`Order #${order.number} → ${status}`, { description: status !== 'received' ? 'Customer notified by email.' : undefined });
      load();
    } catch (err) {
      toast.error('Update failed', { description: formatApiError(err.response?.data?.detail) });
    }
  };

  if (orders === null) return <div className="py-14 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-leaf" /></div>;
  if (orders.length === 0) return (
    <div className="rounded-3xl border border-dashed border-border p-14 text-center text-muted-foreground">
      <Inbox className="h-8 w-8 mx-auto mb-3" /> No orders yet — invited customers can order from their portal.
    </div>
  );

  return (
    <div className="grid gap-4" data-testid="admin-orders-list">
      {orders.map((o) => (
        <div key={o.id} className="p-6 rounded-3xl bg-card border border-border/70 shadow-soft grid md:grid-cols-12 gap-4 items-start" data-testid={`admin-order-${o.number}`}>
          <div className="md:col-span-3">
            <div className="flex items-center gap-2">
              <span className="font-display text-lg font-semibold text-primary">#{o.number}</span>
              <OrderStatusBadge status={o.status} />
            </div>
            <div className="text-sm font-medium text-primary mt-1.5">{o.org || o.customer_name}</div>
            <div className="text-sm text-muted-foreground">{o.customer_name}</div>
            <a href={`mailto:${o.customer_email}`} className="text-sm text-leaf hover:underline underline-offset-4">{o.customer_email}</a>
            <div className="text-xs text-muted-foreground mt-1">{fmtDate(o.created_at)}</div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Items</div>
            <ul className="mt-1 space-y-1">
              {o.items.map((i) => (
                <li key={i.slug} className="text-sm text-muted-foreground flex justify-between gap-3">
                  <span className="truncate">{i.name}</span>
                  <span className="font-medium text-primary shrink-0">{i.cases}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Delivery / Notes</div>
            <p className="text-sm text-muted-foreground mt-1">{o.delivery_address || '—'}</p>
            {o.notes && <p className="text-sm text-muted-foreground mt-1 italic">"{o.notes}"</p>}
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Status</div>
            <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
              <SelectTrigger className="h-10 rounded-xl" data-testid={`order-status-select-${o.number}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
};

const LeadsTab = () => {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    api.get('/api/admin/leads').then((res) => setLeads(res.data.leads)).catch(() => setLeads([]));
  }, []);

  if (leads === null) return <div className="py-14 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-leaf" /></div>;
  if (leads.length === 0) return (
    <div className="rounded-3xl border border-dashed border-border p-14 text-center text-muted-foreground">
      <Inbox className="h-8 w-8 mx-auto mb-3" /> No leads yet.
    </div>
  );

  return (
    <div className="grid gap-4" data-testid="admin-leads-list">
      {leads.map((l) => (
        <div key={l.id} className="rounded-3xl bg-card border border-border/70 shadow-soft p-6 grid md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <div className="font-display text-lg font-semibold text-primary">{l.name}</div>
            <div className="text-sm text-muted-foreground">{l.role}</div>
            <div className="text-xs text-muted-foreground mt-1">{fmtDate(l.created_at)}</div>
          </div>
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Organization</div>
            <div className="text-sm font-medium text-primary mt-0.5">{l.org}</div>
            <a href={`mailto:${l.email}`} className="text-sm text-leaf hover:underline underline-offset-4">{l.email}</a>
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Weekly volume</div>
            <div className="text-sm font-medium text-primary mt-0.5">{l.volume || '—'}</div>
            <div className="mt-2">
              {l.email_sent ? (
                <Badge className="bg-leaf/15 text-leaf border-0 gap-1"><MailCheck className="h-3 w-3" /> Emailed</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground gap-1"><MailX className="h-3 w-3" /> Email failed</Badge>
              )}
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Message</div>
            <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{l.msg || '—'}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const InvitesTab = () => {
  const [invites, setInvites] = useState(null);
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/api/admin/invites');
    setInvites(data.invites);
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/api/admin/invites', { email, org });
      toast.success('Invitation created', {
        description: data.invite.email_sent ? `Invite emailed to ${email}.` : 'Email failed — copy the link below and share it manually.',
      });
      setEmail(''); setOrg('');
      load();
    } catch (err) {
      toast.error('Could not create invite', { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied');
  };

  const stateBadge = (s) => ({
    pending: <Badge className="bg-accent/20 text-accent-foreground border-0">Pending</Badge>,
    accepted: <Badge className="bg-leaf/15 text-leaf border-0">Accepted</Badge>,
    expired: <Badge variant="outline" className="text-muted-foreground">Expired</Badge>,
  }[s]);

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <form onSubmit={sendInvite} className="lg:col-span-4 p-6 rounded-3xl bg-card border border-border/70 shadow-soft h-fit space-y-4" data-testid="invite-create-form">
        <h3 className="font-display text-xl font-semibold text-primary">Invite a customer</h3>
        <div>
          <Label htmlFor="inv-email" className="text-primary mb-1.5">Customer email</Label>
          <Input id="inv-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="buyer@university.ca" className="h-11 rounded-xl" data-testid="invite-email-input" />
        </div>
        <div>
          <Label htmlFor="inv-org" className="text-primary mb-1.5">Company / organization</Label>
          <Input id="inv-org" required value={org} onChange={(e) => setOrg(e.target.value)}
            placeholder="University of Toronto" className="h-11 rounded-xl" data-testid="invite-org-input" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="invite-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-1.5" /> Send invitation</>)}
        </Button>
        <p className="text-xs text-muted-foreground">The customer receives an email link to set their password. Invitations expire after 7 days.</p>
      </form>

      <div className="lg:col-span-8 grid gap-3" data-testid="admin-invites-list">
        {invites === null ? (
          <div className="py-14 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-leaf" /></div>
        ) : invites.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-14 text-center text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-3" /> No invitations sent yet.
          </div>
        ) : invites.map((inv) => (
          <div key={inv.id} className="p-5 rounded-2xl bg-card border border-border/70 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
              <div className="font-medium text-primary truncate">{inv.email}</div>
              <div className="text-sm text-muted-foreground">{inv.org} · {fmtDate(inv.created_at)}</div>
            </div>
            <div className="flex items-center gap-2">
              {stateBadge(inv.state)}
              {inv.state === 'pending' && (
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyLink(inv.invite_link)} data-testid={`copy-invite-${inv.email}`}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />
      <div className="pt-28 md:pt-36 pb-24 container mx-auto" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-script text-2xl text-leaf">admin</div>
            <h1 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">Dashboard.</h1>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => setRefreshKey((k) => k + 1)} data-testid="dashboard-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        <Tabs defaultValue="orders" className="mt-10" key={refreshKey}>
          <TabsList className="rounded-full h-11 p-1">
            <TabsTrigger value="orders" className="rounded-full px-5" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full px-5" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="invites" className="rounded-full px-5" data-testid="tab-invites">Invites</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-8"><OrdersTab /></TabsContent>
          <TabsContent value="leads" className="mt-8"><LeadsTab /></TabsContent>
          <TabsContent value="invites" className="mt-8"><InvitesTab /></TabsContent>
        </Tabs>
      </div>
      <Footer />
    </main>
  );
}
